import { DropDownButton } from 'devextreme-react';
import { IDropDownButtonOptions } from 'devextreme-react/drop-down-button';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReportViewer, {
  RequestOptions,
  ExportSettings,
  Callbacks,
} from 'devexpress-reporting-react/dx-report-viewer';
import 'devextreme/dist/css/dx.light.css';
import '@devexpress/analytics-core/dist/css/dx-analytics.common.css';
import '@devexpress/analytics-core/dist/css/dx-analytics.light.css';

import { fetchSetup } from '@devexpress/analytics-core/analytics-utils-native';
import { QueryFactory, ReportListDto } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import ReactDOM from 'react-dom';
import { StringParam, withDefault } from 'react-router-url-params';
import { UniStorageFactory } from '../../helpers/uni-storage';
import { extractTenantDomainFromUrl } from '@lmt/unicorn-shared/app/helpers/tenant-domain-helper';
import './reporting-dx.css';

const StatisticsViewer: React.FC = React.memo(() => {
  const i18n = useScopedTranslation('reporting.viewer');
  const tenantDomain = extractTenantDomainFromUrl();

  useEffect(() => {
    fetchSetup.fetchSettings.headers = {
      'Accept-Language': i18n.i18n.language,
      'Tenant-Domain': tenantDomain,
    };
  }, [i18n.i18n.language]);

  const reports =
    QueryFactory.StatisticsViewerQuery.useGetReportsQuery().data?.data ??
    ([] as ReportListDto[]);

  const [currentReportName, setCurrentReportName] = useQueryParamInTab<
    string | null
  >('reportUrl', withDefault(StringParam, null));

  const reportUrl = useMemo(() => currentReportName ?? '', [currentReportName]);
  const host = UniStorageFactory.UniStorage.BackendUri.replace(/\/?$/, '/');
  const containerRef = useRef<HTMLDivElement>(null);
  const [toolbarEl, setToolbarEl] = useState<HTMLElement | null>(null);

  /**
   * We have to use a MutationObserver + ReactDOM.createPortal hack to inject
   * our custom “Open Report” dropdown into the DevExpress ReportViewer toolbar.
   * The official callbacks (CustomizeElements/CustomizeToolbarActions) do not
   * support adding a completely new OpenReport element with our own items.
   * With this approach:
   *   • the native toolbar buttons remain center-aligned;
   *   • we keep CSP strict (no unsafe-eval).
   * This workaround is the best available option until DevExpress provides
   * a built-in API for inserting custom toolbar items.
   */
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const observer = new MutationObserver(() => {
      const tb = root.querySelector<HTMLElement>(
        '.dxrd-toolbar[role="toolbar"]',
      );
      if (tb) {
        setToolbarEl(tb);
        observer.disconnect();
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const menuItems: IDropDownButtonOptions[] = reports.map((r) => ({
    text: r.fileName,
  }));
  const portalButton = (
    <div className="dxrd-toolbar-item">
      <DropDownButton
        hint={i18n.t('open_report')}
        items={menuItems}
        onInitialized={(e) => {
          e.component?.option('stylingMode', 'text');
        }}
        onOptionChanged={({ name, value, component }) => {
          if (name === 'opened') {
            component.option('stylingMode', value ? 'contained' : 'text');
          }
          component.option('key', name);
        }}
        dropDownOptions={{
          width: 180,
          animation: {
            show: { type: 'fade', duration: 0 },
            hide: { type: 'fade', duration: 0 },
          },
        }}
        displayExpr="text"
        text={currentReportName ?? i18n.t('open_report')}
        className="reporting-custom-toolbar-item"
        focusStateEnabled={false}
        onItemClick={(e) => {
          setCurrentReportName(e.itemData.text);
        }}
      />
    </div>
  );

  return (
    <div className="statistics-viewer-container" ref={containerRef}>
      {toolbarEl && ReactDOM.createPortal(portalButton, toolbarEl)}
      <ReportViewer cssClass="viewer-flex" reportUrl={reportUrl} height="100%">
        <RequestOptions host={host} invokeAction="/DXXRDVUNI" />
        <ExportSettings useSameTab useAsynchronousExport />
        <Callbacks
          CustomizeExportOptions={({ args }: any) => {
            args.UseSameTab = true;
            args.UseAsynchronousExport = true;
          }}
        />
      </ReportViewer>
    </div>
  );
});

export default StatisticsViewer;
